import { sendAuthRequest } from '../api';
import { BROWSER_AGENT_CONFIG_URL, SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import { getMCPToolName } from 'src/pages/workflow-builder-edit/autonomous-agent/action-drawer/helpers';
import {
  Action,
  ActionBuilderAction,
  ActionBuilderActionRequest,
  ActionBuilderCollectionTemplates,
  ActionFields,
  ActionUpdateImpactResponse,
  ActionUsagesResponse,
  ActiveIntegrationsInterface,
  AddOrUpdateActionInterface,
  AuthorizationConfig,
  BrowserAgentSecret,
  ContextVariable,
  ContextVariableUsagesResponse,
  ConversationOauthClient,
  ConversationOauthCredentialResponse,
  CreateContextVariableRequest,
  CreateConversationOauthClientRequest,
  ProxyTestRequest,
  UpdateActionSecrets,
  UpdateConversationOauthClientRequest,
  ValidateJmesPathRequest,
  ValidateJmesPathResponse,
} from 'src/types/actionBuilderApiTypes';
import { normalizeResponseErrors } from 'src/utils/normalizeResponse';

export const getActionBuilderActionsAPI: () => Promise<ActionBuilderAction> =
  async () => {
    return sendAuthRequest(
      `${API_URL}dashboard-controls/solve/v2/workflow-builder/actions`,
      null,
      'GET',
    )
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const getActionBuilderActiveIntegrationsAPI: () => Promise<ActiveIntegrationsInterface> =
  async () => {
    return sendAuthRequest(
      `${API_URL}dashboard-controls/solve/v2/action-builder/integrations`,
      null,
      'GET',
    )
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const postActionBuilderProxyTestAPI = (
  data: ProxyTestRequest,
): Promise<Response> => {
  return sendAuthRequest(
    `${API_URL}dashboard-controls/solve/v2/action-builder/proxy`,
    data,
    'POST',
  ).then((res: Response | { error: string }) => normalizeResponseErrors(res));
};

export const postActionBuilderBackgroundProxyTestAPI = (
  data: ProxyTestRequest,
): Promise<Response> => {
  return sendAuthRequest(
    `${API_URL}dashboard-controls/solve/v2/action-builder/proxy-background`,
    data,
    'POST',
  ).then((res: Response | { error: string }) => normalizeResponseErrors(res));
};

export const getActionBuilderBackgroundProxyResult = (
  executionId: string,
): Promise<Response> => {
  return sendAuthRequest(
    `${API_URL}dashboard-controls/solve/v2/action-builder/proxy-background/${executionId}`,
    null,
    'GET',
  ).then((res: Response | { error: string }) => normalizeResponseErrors(res));
};

export const addActionAPI = (
  action: AddOrUpdateActionInterface,
): Promise<ActionBuilderAction> => {
  return sendAuthRequest(
    `${API_URL}dashboard-controls/solve/v2/workflow-builder/action`,
    action,
    'POST',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updateActionAPI = (
  action: AddOrUpdateActionInterface,
  actionId: string,
): Promise<ActionBuilderAction> => {
  return sendAuthRequest(
    `${API_URL}dashboard-controls/solve/v2/workflow-builder/action/${actionId}`,
    action,
    'PUT',
    { action_id: actionId },
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const validateJmesPath = async (
  data: ValidateJmesPathRequest,
): Promise<ValidateJmesPathResponse> => {
  const res = await sendAuthRequest(
    `${API_URL}dashboard-controls/solve/v2/action-builder/jmespath/validate`,
    data,
    'POST',
  );
  const normalizedResponse = await normalizeResponseErrors(res);
  return normalizedResponse.json();
};

const actionBuilderApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createAction: builder.mutation<
      ActionBuilderAction,
      AddOrUpdateActionInterface
    >({
      invalidatesTags: ['Actions', 'Tools'],
      query: body => ({
        body,
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/action',
      }),
    }),
    createContextVariable: builder.mutation<
      ContextVariable,
      CreateContextVariableRequest
    >({
      invalidatesTags: ['ContextVariables'],
      query: body => ({
        body,
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/settings/context-variable',
      }),
    }),
    createConversationOauthClient: builder.mutation<
      ConversationOauthClient,
      CreateConversationOauthClientRequest
    >({
      invalidatesTags: ['ConversationOauthClients'],
      query: body => ({
        body,
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/action-builder/conversation-oauth-client',
      }),
    }),
    createImportedActions: builder.mutation<
      void,
      { openapi_spec: Record<string, unknown> }
    >({
      invalidatesTags: ['ImportedActions'],
      query: body => ({
        body,
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/actions/import',
      }),
    }),
    deleteAction: builder.mutation<
      ActionBuilderAction,
      { action_ids: string[] }
    >({
      invalidatesTags: [
        'Actions',
        'Tools',
        'ContextVariables',
        'ImportedActions',
      ],
      query: body => ({
        body,
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/action/delete',
      }),
    }),
    deleteConversationOauthClient: builder.mutation<void, string>({
      invalidatesTags: ['ConversationOauthClients'],
      query: conversationOauthClientId => ({
        method: 'DELETE',
        url:
          SOLVE_V2_BASE_URL +
          `/action-builder/conversation-oauth-client/${conversationOauthClientId}`,
      }),
    }),
    getActionBuilderActions: builder.query<
      ActionBuilderAction,
      ActionBuilderActionRequest
    >({
      providesTags: ['Actions'],
      query: ({ product, shouldReturnAll }) => ({
        method: 'GET',
        params: {
          product,
          return_all: shouldReturnAll,
        },
        url: SOLVE_V2_BASE_URL + '/workflow-builder/actions',
      }),
      transformResponse: (response: ActionBuilderAction) => {
        const actions = response.actions.map((action: Action) => {
          if (action.action_type === 'mcp') {
            return {
              ...action,
              action_name: getMCPToolName(action.action_name),
            };
          }
          return action;
        });

        return { ...response, actions };
      },
    }),
    getActionBuilderIntegrations: builder.query<
      ActiveIntegrationsInterface,
      void
    >({
      query: () => ({
        method: 'GET',
        url: SOLVE_V2_BASE_URL + '/action-builder/integrations',
      }),
    }),
    getActionBuilderOauthCredential: builder.query<
      ConversationOauthCredentialResponse,
      string
    >({
      query: conversationOauthClientId => ({
        method: 'GET',
        url:
          SOLVE_V2_BASE_URL +
          `/action-builder/conversation-oauth-credential/${conversationOauthClientId}`,
      }),
    }),
    getActionSecrets: builder.query<BrowserAgentSecret[], string>({
      providesTags: ['ActionSecrets'],
      query: actionId => ({
        method: 'GET',
        url: BROWSER_AGENT_CONFIG_URL + `/${actionId}/secrets`,
      }),
    }),
    getActionUpdateImpact: builder.mutation<
      ActionUpdateImpactResponse,
      { actionId: string; body: { action_fields: ActionFields } }
    >({
      // Read-only preflight (POST for the proposed-fields body): computes the
      // blast radius of an API action edit under reference resolution.
      query: ({ actionId, body }) => ({
        body,
        method: 'POST',
        url:
          SOLVE_V2_BASE_URL +
          '/workflow-builder/action/' +
          actionId +
          '/update-impact',
      }),
    }),
    getActionUsages: builder.query<ActionUsagesResponse, void>({
      providesTags: ['ActionUsages'],
      query: () => ({
        method: 'GET',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/actions/usages',
      }),
    }),
    getAuthConfigs: builder.query<AuthorizationConfig[], void>({
      providesTags: ['AuthConfigs'],
      query: () => ({
        method: 'GET',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/settings/auth-configs',
      }),
    }),
    getCollectionTemplates: builder.query<
      ActionBuilderCollectionTemplates,
      void
    >({
      query: () => ({
        method: 'GET',
        url: `${SOLVE_V2_BASE_URL}/action-builder/collections/templates`,
      }),
    }),
    getContextVariableUsages: builder.query<
      ContextVariableUsagesResponse,
      void
    >({
      providesTags: ['ContextVariableUsages'],
      query: () => ({
        method: 'GET',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/context-variables/usages',
      }),
    }),
    getConversationOauthClients: builder.query<ConversationOauthClient[], void>(
      {
        providesTags: ['ConversationOauthClients'],
        query: () => ({
          method: 'GET',
          url: SOLVE_V2_BASE_URL + '/action-builder/conversation-oauth-clients',
        }),
      },
    ),
    getImportedActions: builder.query<ActionBuilderAction, void>({
      providesTags: ['ImportedActions'],
      query: () => ({
        method: 'GET',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/actions/imported',
      }),
    }),
    getZendeskActions: builder.query<ActionBuilderAction, void>({
      providesTags: ['ZendeskActions'],
      query: () => ({
        method: 'GET',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/actions/zendesk',
      }),
    }),
    updateAction: builder.mutation<
      ActionBuilderAction,
      { actionId: string; body: AddOrUpdateActionInterface }
    >({
      // 'Workflow': API action fields resolve by reference, so an action edit
      // changes referencing workflows' effective validation (undefined CVs,
      // parent-provided CVs). Canvas responses must refetch or banners like
      // the building-block validation banner keep showing pre-edit state.
      invalidatesTags: ['Actions', 'Tools', 'Workflow'],
      query: ({ actionId, body }) => ({
        body,
        method: 'PUT',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/action/' + actionId,
      }),
    }),
    updateActionSecrets: builder.mutation<
      BrowserAgentSecret[],
      UpdateActionSecrets
    >({
      invalidatesTags: ['ActionSecrets'],
      async onQueryStarted({ actionId }, { dispatch, queryFulfilled }) {
        // Possible that on refetch, after invalidating tag may not still have data
        // This is to update the cache for getActionSecrets right away with this result
        const { data } = await queryFulfilled;
        dispatch(
          actionBuilderApi.util.updateQueryData(
            'getActionSecrets',
            actionId,
            () => data,
          ),
        );
      },
      query: ({ actionId, secrets }) => ({
        body: secrets,
        method: 'PUT',
        url: BROWSER_AGENT_CONFIG_URL + `/${actionId}/secrets`,
      }),
    }),
    updateAuthConfigs: builder.mutation<
      { success: boolean },
      AuthorizationConfig[]
    >({
      invalidatesTags: ['AuthConfigs'],
      query: body => ({
        body,
        method: 'PUT',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/settings/auth-configs',
      }),
    }),
    updateContextVariable: builder.mutation<
      ContextVariable,
      { body: CreateContextVariableRequest; contextVariableId: string }
    >({
      invalidatesTags: ['ContextVariables'],
      query: ({ body, contextVariableId }) => ({
        body,
        method: 'PUT',
        url:
          SOLVE_V2_BASE_URL +
          `/workflow-builder/settings/context-variable/${contextVariableId}`,
      }),
    }),
    updateContextVariables: builder.mutation<
      { success: boolean },
      ContextVariable[]
    >({
      invalidatesTags: ['ContextVariables'],
      query: body => ({
        body,
        method: 'PUT',
        url: SOLVE_V2_BASE_URL + '/workflow-builder/context-variables',
      }),
    }),
    updateConversationOauthClient: builder.mutation<
      ConversationOauthClient,
      {
        body: UpdateConversationOauthClientRequest;
        conversationOauthClientId: string;
      }
    >({
      invalidatesTags: ['ConversationOauthClients'],
      query: ({ body, conversationOauthClientId }) => ({
        body,
        method: 'PUT',
        url:
          SOLVE_V2_BASE_URL +
          `/action-builder/conversation-oauth-client/${conversationOauthClientId}`,
      }),
    }),
  }),
});

export const {
  useCreateActionMutation,
  useCreateContextVariableMutation,
  useCreateConversationOauthClientMutation,
  useCreateImportedActionsMutation,
  useDeleteActionMutation,
  useDeleteConversationOauthClientMutation,
  useGetActionBuilderActionsQuery,
  useGetActionBuilderIntegrationsQuery,
  useGetActionSecretsQuery,
  useGetActionUpdateImpactMutation,
  useGetActionUsagesQuery,
  useGetAuthConfigsQuery,
  useGetCollectionTemplatesQuery,
  useGetContextVariableUsagesQuery,
  useGetConversationOauthClientsQuery,
  useGetImportedActionsQuery,
  useGetZendeskActionsQuery,
  useLazyGetActionBuilderOauthCredentialQuery,
  useUpdateActionMutation,
  useUpdateActionSecretsMutation,
  useUpdateAuthConfigsMutation,
  useUpdateContextVariableMutation,
  useUpdateContextVariablesMutation,
  useUpdateConversationOauthClientMutation,
} = actionBuilderApi;
